import React from 'react';
import { getPasswordRequirements, calculatePasswordStrength } from '../../utils/passwordValidation';

interface PasswordStrengthMeterProps {
  password: string;
  showRequirements?: boolean;
}

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = ({
  password,
  showRequirements = true,
}) => {
  const strength = calculatePasswordStrength(password);
  const requirements = getPasswordRequirements(password);

  // Don't show anything if password is empty
  if (!password) {
    return null;
  }

  // Determine strength bar color and width
  const getStrengthConfig = () => {
    switch (strength) {
      case 'weak':
        return {
          color: 'bg-red-500',
          width: 'w-1/3',
          label: 'Weak',
          textColor: 'text-red-600',
        };
      case 'medium':
        return {
          color: 'bg-yellow-500',
          width: 'w-2/3',
          label: 'Medium',
          textColor: 'text-yellow-600',
        };
      case 'strong':
        return {
          color: 'bg-green-500',
          width: 'w-full',
          label: 'Strong',
          textColor: 'text-green-600',
        };
    }
  };

  const config = getStrengthConfig();

  return (
    <div className="mt-2 space-y-2">
      {/* Strength bar */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-gray-600">Password strength:</span>
          <span className={`text-xs font-semibold ${config.textColor}`}>
            {config.label}
          </span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={`h-full ${config.color} transition-all duration-300 ease-in-out ${config.width}`}
          />
        </div>
      </div>

      {/* Requirements checklist */}
      {showRequirements && (
        <div className="space-y-1">
          <p className="text-xs text-gray-600 font-medium">Password must contain:</p>
          {requirements.map((req, index) => (
            <div key={index} className="flex items-center space-x-2">
              {req.met ? (
                <svg
                  className="w-4 h-4 text-green-500 flex-shrink-0"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4 text-gray-400 flex-shrink-0"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <circle cx="12" cy="12" r="10" />
                </svg>
              )}
              <span
                className={`text-xs ${
                  req.met ? 'text-green-600 font-medium' : 'text-gray-600'
                }`}
              >
                {req.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthMeter;
